import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Multi-select over an ordered list of ids.
 *
 * The first selection model in the app — Tasks' BulkActions deliberately acts on
 * whatever the filters are showing rather than on a picked set, which is a
 * different affordance. Here you pick.
 *
 * `orderedIds` must be the ids *currently on screen, in display order*, so a
 * shift-range selects what you visually dragged across rather than some
 * underlying unsorted order.
 */
export function useFileSelection(orderedIds) {
    const [selected, setSelected] = useState(EMPTY);
    // The last plain click. Shift-clicking extends from here, the way every
    // file manager behaves.
    const anchorRef = useRef(null);

    const idsKey = orderedIds.join(' ');

    // Drop ids that no longer exist — a file deleted by the agent, or filtered
    // out then removed. Without this a bulk action fires against ghosts and
    // reports failures for rows that aren't there.
    const liveIds = useMemo(() => new Set(orderedIds), [idsKey]); // eslint-disable-line
    useEffect(() => {
        setSelected((prev) => {
            if (prev.size === 0) return prev;
            const next = new Set();
            for (const id of prev) if (liveIds.has(id)) next.add(id);
            return next.size === prev.size ? prev : next;
        });
    }, [liveIds]);

    const clear = useCallback(() => {
        anchorRef.current = null;
        setSelected((prev) => (prev.size === 0 ? prev : EMPTY));
    }, []);

    const toggle = useCallback((id, shiftKey) => {
        setSelected((prev) => {
            const next = new Set(prev);

            if (shiftKey && anchorRef.current != null) {
                const from = orderedIds.indexOf(anchorRef.current);
                const to = orderedIds.indexOf(id);
                if (from !== -1 && to !== -1) {
                    // Additive, not replace — matches Finder and VS Code, and
                    // means two separate ranges can be built up.
                    for (let i = Math.min(from, to); i <= Math.max(from, to); i++) {
                        next.add(orderedIds[i]);
                    }
                    return next;
                }
            }

            if (next.has(id)) next.delete(id);
            else next.add(id);
            anchorRef.current = id;
            return next;
        });
        // eslint-disable-next-line
    }, [idsKey]);

    const selectAll = useCallback(() => {
        anchorRef.current = null;
        setSelected(new Set(orderedIds));
        // eslint-disable-next-line
    }, [idsKey]);

    // Escape clears, guarded on the active element so it doesn't fight an open
    // rename field or the search box.
    useEffect(() => {
        if (selected.size === 0) return undefined;
        const handleKey = (e) => {
            if (e.key !== 'Escape') return;
            const el = document.activeElement;
            if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
            clear();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [selected.size, clear]);

    return {
        selected,
        count: selected.size,
        isSelected: useCallback((id) => selected.has(id), [selected]),
        toggle,
        selectAll,
        clear,
    };
}

const EMPTY = new Set();

export default useFileSelection;
